import { useState } from 'react'
import { CalendarClock, CheckCircle2, Lock, PauseCircle, PlayCircle } from 'lucide-react'
import { write } from '../api'
import { Button, Field, Modal, ReasonField, Refusal } from '../ui'
import { words } from '../format'

// ======================================================================
// THE CLOCK AND STATUS WRITES
// ======================================================================
//
// THE REASON IS THE FORM. Every one of these five is refused by
// er_cases/service._clean_reason without a non-blank reason, and the
// text is read verbatim by everyone with view_case — the subject
// included. So on every dialog here the reason field is the first
// control, it is marked required, and it says who reads it. It is not a
// box under the button.
//
// The submit is enabled only when a reason has been typed. THAT IS A
// COURTESY, NOT A VALIDATION RULE: the server's refusal is the rule, and
// it writes an audit row saying an attempt was made. If the button
// somehow submits blank, the refusal is shown as the server worded it.

export default function CaseActions({ caseId, detail, onUpdated }) {
  const [open, setOpen] = useState(null)
  const paused = !!detail.paused_at
  const closed = detail.status === 'closed'

  // A closed case takes no clock writes. This mirrors the server, which
  // refuses them — it does not replace it.
  const actions = [
    !closed && !paused && { id: 'stage', label: 'Move stage', icon: CheckCircle2 },
    !closed && !paused && { id: 'pause', label: 'Pause', icon: PauseCircle },
    !closed && paused && { id: 'resume', label: 'Resume', icon: PlayCircle },
    !closed && { id: 'target', label: 'Change target date', icon: CalendarClock },
    !closed && !paused && { id: 'close', label: 'Close case', icon: Lock },
  ].filter(Boolean)

  if (!actions.length) return null

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <Button key={a.id} size="sm" icon={a.icon} onClick={() => setOpen(a.id)}>
            {a.label}
          </Button>
        ))}
      </div>

      {open === 'stage' && (
        <StageDialog
          caseId={caseId}
          detail={detail}
          onClose={() => setOpen(null)}
          onUpdated={onUpdated}
        />
      )}
      {open === 'pause' && (
        <ReasonDialog
          caseId={caseId}
          path="pause"
          title="Pause this case"
          subtitle="The clock stops now and the elapsed and remaining figures freeze until it is resumed."
          confirm="Pause the case"
          onClose={() => setOpen(null)}
          onUpdated={onUpdated}
        />
      )}
      {open === 'resume' && (
        <ReasonDialog
          caseId={caseId}
          path="resume"
          title="Resume this case"
          subtitle="The clock restarts and the target close date is extended by however long the case was paused. Both dates are recorded on the timeline."
          confirm="Resume the case"
          onClose={() => setOpen(null)}
          onUpdated={onUpdated}
        />
      )}
      {open === 'close' && (
        <ReasonDialog
          caseId={caseId}
          path="close"
          title="Close this case"
          subtitle="Content stops changing. Access can still be granted afterwards — an appeal manager appointed later, or somebody answering a subject access request."
          confirm="Close the case"
          onClose={() => setOpen(null)}
          onUpdated={onUpdated}
        />
      )}
      {open === 'target' && (
        <TargetDialog
          caseId={caseId}
          detail={detail}
          onClose={() => setOpen(null)}
          onUpdated={onUpdated}
        />
      )}
    </>
  )
}

function useSubmit(onUpdated, onClose) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const run = async (fn) => {
    setBusy(true)
    setError(null)
    try {
      const next = await fn()
      onUpdated(next)
      onClose()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }
  return { busy, error, setError, run }
}

function ReasonDialog({ caseId, path, title, subtitle, confirm, onClose, onUpdated }) {
  const [reason, setReason] = useState('')
  const { busy, error, setError, run } = useSubmit(onUpdated, onClose)

  return (
    <Modal open onClose={onClose} title={title} subtitle={subtitle}>
      <Refusal error={error} onDismiss={() => setError(null)} />
      <ReasonField value={reason} onChange={setReason} autoFocus />
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!reason.trim()}
          onClick={() =>
            run(() => write(`/${caseId}/${path}`, { body: { reason } }))
          }
        >
          {confirm}
        </Button>
      </div>
    </Modal>
  )
}

function StageDialog({ caseId, detail, onClose, onUpdated }) {
  const [toStage, setToStage] = useState('')
  const [reason, setReason] = useState('')
  const { busy, error, setError, run } = useSubmit(onUpdated, onClose)

  // THE OPTIONS ARE THE CASE TYPE'S OWN STAGES, echoed on the detail
  // response. There is no stage list in this application.
  const options = detail.stages.filter((s) => s.key !== detail.current_stage)

  return (
    <Modal
      open
      onClose={onClose}
      title="Move this case to another stage"
      subtitle={`Currently at ${words(detail.current_stage)}.`}
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      {options.length === 0 ? (
        <p className="text-[12.5px] leading-relaxed text-muted">
          This case type has no other configured stage to move to.
        </p>
      ) : (
        <>
          <ReasonField value={reason} onChange={setReason} autoFocus />

          <div className="mt-4">
            <Field label="Move to" required>
              <div className="space-y-1.5">
                {options.map((s) => (
                  <label
                    key={s.key}
                    className={`flex cursor-pointer items-center gap-3 rounded-md border px-3.5 py-2.5 transition ${
                      toStage === s.key
                        ? 'border-accent-line bg-accent-dim'
                        : 'border-line bg-surface-2 hover:border-line-strong'
                    }`}
                  >
                    <input
                      type="radio"
                      name="to_stage"
                      className="accent-[color:var(--accent)]"
                      checked={toStage === s.key}
                      onChange={() => setToStage(s.key)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-head text-[12.5px] font-semibold">
                        {s.label}
                      </span>
                      <span className="block text-[11px] text-faint">
                        target {s.target_days} days
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </Field>
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={!reason.trim() || !toStage}
              onClick={() =>
                run(() =>
                  write(`/${caseId}/stage`, { body: { to_stage: toStage, reason } }),
                )
              }
            >
              Move stage
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}

function TargetDialog({ caseId, detail, onClose, onUpdated }) {
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const { busy, error, setError, run } = useSubmit(onUpdated, onClose)

  return (
    <Modal
      open
      onClose={onClose}
      title="Change the target close date"
      subtitle="Both the old and the new date are recorded on the case timeline, with your reason, where the subject can read them."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <ReasonField value={reason} onChange={setReason} autoFocus />

      <div className="mt-4">
        <Field
          label="New target date"
          required
          hint={
            detail.target_close_at
              ? `Currently ${new Date(detail.target_close_at).toISOString().slice(0, 10)}.`
              : 'This case has no target date at present.'
          }
        >
          {/* Sent as the raw string the field produces. The service
              parses it — a date this client could not parse must still
              reach the handler, be refused there, and leave an audit row
              saying a deadline was nearly moved. */}
          <input
            type="date"
            className="field"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!reason.trim() || !value}
          onClick={() =>
            run(() =>
              write(`/${caseId}/target`, { body: { new_target: value, reason } }),
            )
          }
        >
          Change the date
        </Button>
      </div>
    </Modal>
  )
}
